/**
 * LegalTermsGate — Modal bloqueante de termos legais.
 *
 * Renderizado ANTES do OnboardingTour, APÓS login autenticado.
 * Médico deve aceitar explicitamente antes de usar a app.
 * Estética Claude Design: Plus Jakarta Sans + violet/cta-blue + ink tones.
 *
 * Acessibilidade:
 *   - role="dialog" + aria-modal + aria-labelledby
 *   - Focus trap interno (Tab cicla dentro do modal)
 *   - Esc NÃO fecha (modal bloqueante — só Recusar/Aceitar)
 *   - Botão Aceitar foca quando checkbox é marcado
 *   - prefers-reduced-motion: animações desativadas
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';

interface LegalTermsGateProps {
  /** Função de tradução do useTranslations() da app */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Callback ao aceitar — capataz chama consent.accept() antes de passar */
  onAccept: () => void;
  /** Callback ao recusar — capataz chama useAuth.logout() */
  onDecline: () => void;
}

// Pontos legais com ícone e chave i18n
const LEGAL_POINTS = [
  { icon: '⚠️', key: 'legalGate.point1' },
  { icon: '⚖️', key: 'legalGate.point2' },
  { icon: '🔒', key: 'legalGate.point3' },
  { icon: '📊', key: 'legalGate.point4' },
  { icon: '🏢', key: 'legalGate.point5' },
  // 29/05/2026: adicionado point6 sobre consentimento email
  // (LGPD/GDPR — antes mandavamos follow-up sem consentimento explicito).
  { icon: '📧', key: 'legalGate.point6' },
] as const;

// Verifica preferência de movimento reduzido
function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function LegalTermsGate({ t, onAccept, onDecline }: LegalTermsGateProps) {
  const [checked, setChecked] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [mounted, setMounted] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);
  const acceptBtnRef = useRef<HTMLButtonElement>(null);
  const declineBtnRef = useRef<HTMLButtonElement>(null);
  const reducedMotion = prefersReducedMotion();

  // Animação de entrada (scale + fade)
  useEffect(() => {
    // Frame duplo garante transição CSS após mount
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setMounted(true));
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Focus trap: Tab e Shift+Tab ficam dentro do modal
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      // Modal bloqueante — Esc não fecha, apenas previne default
      e.preventDefault();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  // Foca o modal ao montar para capturar Tab imediatamente
  useEffect(() => {
    const timeout = setTimeout(() => {
      checkboxRef.current?.focus();
    }, reducedMotion ? 0 : 420);
    return () => clearTimeout(timeout);
  }, [reducedMotion]);

  // Quando checkbox é marcado, move foco ao botão Aceitar
  useEffect(() => {
    if (checked) {
      acceptBtnRef.current?.focus();
    }
  }, [checked]);

  // Sequência de saída cinematic ao aceitar
  const handleAccept = useCallback(() => {
    if (!checked) return;
    if (reducedMotion) {
      onAccept();
      return;
    }
    setLeaving(true);
    setTimeout(() => {
      onAccept();
    }, 320);
  }, [checked, onAccept, reducedMotion]);

  const handleDecline = useCallback(() => {
    if (reducedMotion) {
      onDecline();
      return;
    }
    setLeaving(true);
    setTimeout(() => {
      onDecline();
    }, 320);
  }, [onDecline, reducedMotion]);

  const isLeaving = leaving;
  const isVisible = mounted && !isLeaving;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Plus+Jakarta+Sans:wght@400&family=IBM+Plex+Mono:wght@400;500&display=swap');

        .legal-gate-backdrop {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(34, 37, 50, 0.97);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          opacity: 0;
          transition: ${reducedMotion ? 'none' : 'opacity 300ms ease'};
        }

        .legal-gate-backdrop.visible {
          opacity: 1;
        }

        .legal-gate-backdrop.leaving {
          opacity: 0;
        }

        .legal-gate-card {
          position: relative;
          max-width: 640px;
          width: 100%;
          background: var(--ink-soft, #2A2C38);
          border: 1.5px solid rgba(124,124,248,0.30);
          border-radius: 4px;
          padding: 32px;
          box-shadow:
            0 0 24px rgba(124, 124, 248, 0.12),
            0 0 48px rgba(124, 124, 248, 0.05),
            inset 0 0 40px rgba(34, 37, 50, 0.6);
          transform: ${reducedMotion ? 'none' : 'scale(0.96)'};
          opacity: 0;
          transition: ${reducedMotion ? 'none' : 'transform 400ms ease-out, opacity 400ms ease-out'};
          max-height: 90vh;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #7C7CF8 var(--ink-soft, #2A2C38);
        }

        .legal-gate-card.visible {
          transform: scale(1);
          opacity: 1;
        }

        .legal-gate-card.leaving {
          transform: scale(0.96);
          opacity: 0;
        }

        .legal-gate-card::-webkit-scrollbar {
          width: 6px;
        }
        .legal-gate-card::-webkit-scrollbar-track {
          background: var(--ink-soft, #2A2C38);
        }
        .legal-gate-card::-webkit-scrollbar-thumb {
          background: #7C7CF8;
          border-radius: 3px;
        }

        .legal-gate-header {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 6px;
        }

        .legal-gate-pera-icon {
          width: 40px;
          height: 40px;
          flex-shrink: 0;
          object-fit: contain;
          border-radius: 50%;
          filter: drop-shadow(0 0 8px rgba(124, 124, 248, 0.45));
        }

        .legal-gate-title {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #FFFFFF;
          line-height: 1.6;
          text-shadow: none;
          margin: 0;
          letter-spacing: 0.02em;
        }

        .legal-gate-subtitle {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 14px;
          color: var(--ink-light, #C7C8CE);
          margin: 0 0 20px 54px;
          letter-spacing: 0.03em;
        }

        .legal-gate-points {
          list-style: none;
          padding: 0;
          margin: 0 0 16px 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .legal-gate-point {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px;
          color: var(--ink-light, #C7C8CE);
          line-height: 1.55;
          display: flex;
          gap: 10px;
          align-items: flex-start;
        }

        .legal-gate-point-icon {
          flex-shrink: 0;
          font-size: 15px;
          margin-top: 1px;
        }

        .legal-gate-separator {
          height: 1px;
          background: rgba(124,124,248,0.20);
          margin: 16px 0;
        }

        .legal-gate-links-line {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12px;
          color: var(--ink-mute, #929399);
          margin-bottom: 20px;
        }

        .legal-gate-link {
          color: #7C7CF8;
          text-decoration: underline;
          text-underline-offset: 3px;
          cursor: pointer;
          transition: color 150ms;
        }

        .legal-gate-link:hover,
        .legal-gate-link:focus {
          color: #a0a0ff;
          outline: none;
        }

        /* Checkbox pixel-art retro */
        .legal-gate-checkbox-wrapper {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 24px;
          cursor: pointer;
        }

        .legal-gate-checkbox-wrapper:focus-within .legal-gate-checkbox-visual {
          box-shadow: 0 0 0 2px rgba(124,124,248,0.6), 0 0 8px rgba(124,124,248,0.3);
        }

        .legal-gate-checkbox-input {
          position: absolute;
          opacity: 0;
          width: 1px;
          height: 1px;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
        }

        .legal-gate-checkbox-visual {
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          border: 2px solid rgba(124,124,248,0.45);
          border-radius: 2px;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px;
          color: #7C7CF8;
          margin-top: 2px;
          transition: ${reducedMotion ? 'none' : 'border-color 150ms, background 150ms, box-shadow 150ms'};
        }

        .legal-gate-checkbox-visual.checked {
          border-color: #7C7CF8;
          background: rgba(124,124,248,0.10);
        }

        .legal-gate-checkbox-label {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px;
          color: var(--ink-light, #C7C8CE);
          line-height: 1.5;
          user-select: none;
        }

        /* Botões */
        .legal-gate-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }

        .legal-gate-btn-accept {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.04em;
          color: #FFFFFF;
          background: #0060F0;
          border: 1px solid #0060F0;
          border-radius: 2px;
          padding: 12px 18px;
          cursor: pointer;
          flex: 1;
          min-width: 180px;
          text-align: center;
          line-height: 1.6;
          box-shadow: none;
          transition: ${reducedMotion ? 'none' : 'opacity 150ms, box-shadow 200ms, border-color 200ms'};
        }

        .legal-gate-btn-accept:not(:disabled):hover {
          box-shadow: 0 0 14px rgba(255,106,0,0.8), 0 0 32px rgba(255,106,0,0.5);
          border-color: #FF6A00;
          color: #fff;
        }

        .legal-gate-btn-accept:disabled {
          opacity: 0.35;
          cursor: not-allowed;
          background: #0060F0;
          box-shadow: none;
        }

        .legal-gate-btn-accept:not(:disabled):focus {
          outline: 2px solid rgba(124,124,248,0.7);
          outline-offset: 3px;
        }

        .legal-gate-btn-decline {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px;
          color: var(--ink-light, #C7C8CE);
          background: transparent;
          border: 1px solid rgba(124,124,248,0.35);
          border-radius: 2px;
          padding: 10px 18px;
          cursor: pointer;
          white-space: nowrap;
          transition: ${reducedMotion ? 'none' : 'background 150ms, color 150ms, border-color 150ms'};
        }

        .legal-gate-btn-decline:hover {
          background: rgba(124,124,248,0.08);
          border-color: rgba(124,124,248,0.60);
          color: #fff;
        }

        .legal-gate-btn-decline:focus {
          outline: 2px solid rgba(124,124,248,0.60);
          outline-offset: 3px;
        }
      `}</style>

      {/* Backdrop */}
      <div
        className={`legal-gate-backdrop${isVisible ? ' visible' : ''}${isLeaving ? ' leaving' : ''}`}
        aria-hidden="true"
      >
        {/* Card modal */}
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="legal-gate-title"
          className={`legal-gate-card${isVisible ? ' visible' : ''}${isLeaving ? ' leaving' : ''}`}
          onKeyDown={handleKeyDown}
        >
          {/* Cabeçalho */}
          <div className="legal-gate-header">
            <img
              className="legal-gate-pera-icon"
              src="/icons/icon-48.png"
              alt=""
              aria-hidden="true"
              width={40}
              height={40}
            />
            <h1
              id="legal-gate-title"
              className="legal-gate-title"
            >
              {t('legalGate.title')}
            </h1>
          </div>

          {/* Subtítulo */}
          <p className="legal-gate-subtitle">
            {t('legalGate.subtitle')}
          </p>

          {/* 5 Pontos legais */}
          <ul className="legal-gate-points" aria-label={t('legalGate.title')}>
            {LEGAL_POINTS.map(({ icon, key }) => (
              <li key={key} className="legal-gate-point">
                <span className="legal-gate-point-icon" aria-hidden="true">{icon}</span>
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>

          {/* Separador */}
          <div className="legal-gate-separator" aria-hidden="true" />

          {/* Links termos completos */}
          <p className="legal-gate-links-line">
            {t('legalGate.fullTermsText')}{' '}
            <a
              href="https://drpera.com.br/termos/"
              target="_blank"
              rel="noopener noreferrer"
              className="legal-gate-link"
            >
              {t('legalGate.linkTerms')}
            </a>
            {' '}&{' '}
            <a
              href="https://drpera.com.br/privacidade/"
              target="_blank"
              rel="noopener noreferrer"
              className="legal-gate-link"
            >
              {t('legalGate.linkPrivacy')}
            </a>
          </p>

          {/* Checkbox pixel-art */}
          <label className="legal-gate-checkbox-wrapper">
            <input
              ref={checkboxRef}
              type="checkbox"
              className="legal-gate-checkbox-input"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              aria-label={t('legalGate.checkboxLabel')}
            />
            <span
              className={`legal-gate-checkbox-visual${checked ? ' checked' : ''}`}
              aria-hidden="true"
            >
              {checked ? '✓' : ''}
            </span>
            <span className="legal-gate-checkbox-label">
              {t('legalGate.checkboxLabel')}
            </span>
          </label>

          {/* Botões de ação */}
          <div className="legal-gate-actions">
            <button
              ref={acceptBtnRef}
              className="legal-gate-btn-accept"
              onClick={handleAccept}
              disabled={!checked}
              aria-disabled={!checked}
            >
              {t('legalGate.ctaAccept')}
            </button>
            <button
              ref={declineBtnRef}
              className="legal-gate-btn-decline"
              onClick={handleDecline}
            >
              {t('legalGate.ctaDecline')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
